/** Enhances server-rendered collection page links without requiring JavaScript. */
class CollectionPagination extends HTMLElement {
  #request = null;

  connectedCallback() {
    this.addEventListener('click', this.#loadMore);
    document.addEventListener('filter:update', this.#abortRequest);
    window.addEventListener('popstate', this.#abortRequest);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#loadMore);
    document.removeEventListener('filter:update', this.#abortRequest);
    window.removeEventListener('popstate', this.#abortRequest);
    this.#abortRequest();
  }

  #finishRequest(request) {
    if (this.#request !== request) return;
    request.grid.removeAttribute('aria-busy');
    request.link.removeAttribute('aria-disabled');
    request.link.textContent = request.link.hasAttribute('data-load-more-fallback') ? 'Open next page' : 'Load More';
    this.#request = null;
  }

  #abortRequest = () => {
    if (!this.#request) return;
    const request = this.#request;
    request.controller.abort();
    this.#finishRequest(request);
  };

  #loadMore = async (event) => {
    if (
      event.defaultPrevented || event.button !== 0 ||
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
      !(event.target instanceof Element)
    ) return;

    const link = event.target.closest('a[data-load-more]');
    if (!link || !this.contains(link) || link.hasAttribute('data-load-more-fallback')) return;

    const results = this.closest('results-list');
    const grid = results?.querySelector('[ref="grid"]');
    const sectionId = results?.getAttribute('section-id');
    const status = this.querySelector('[role="status"]');
    if (!grid || !sectionId || !status) return;

    const nextUrl = new URL(link.href, window.location.href);
    // Leave unexpected destinations to ordinary browser navigation.
    if (nextUrl.origin !== window.location.origin || nextUrl.pathname !== window.location.pathname) return;

    event.preventDefault();
    if (this.#request) return;

    const request = {
      controller: new AbortController(),
      grid,
      link,
      pageUrl: window.location.href,
    };
    this.#request = request;
    const expectedPage = Number(this.dataset.currentPage) + 1;
    const isCurrent = () => this.#request === request && !request.controller.signal.aborted &&
      this.isConnected && results.querySelector('[ref="grid"]') === grid &&
      window.location.href === request.pageUrl;

    link.textContent = 'Loading...';
    link.setAttribute('aria-disabled', 'true');
    grid.setAttribute('aria-busy', 'true');
    status.textContent = '';

    try {
      const response = await fetch(nextUrl.href, {
        credentials: 'same-origin',
        signal: request.controller.signal,
      });
      if (!response.ok) throw new Error('Collection page could not be loaded.');

      const html = await response.text();
      if (!isCurrent()) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextResults = Array.from(doc.querySelectorAll('results-list'))
        .find((element) => element.getAttribute('section-id') === sectionId);
      const nextGrid = nextResults?.querySelector('[ref="grid"]');
      const nextPagination = nextResults?.querySelector('collection-pagination');
      const cards = nextGrid ? Array.from(nextGrid.children)
        .filter((element) => element.matches('.product-grid__item[data-product-id]')) : [];

      if (
        !nextPagination || Number(nextPagination.dataset.currentPage) !== expectedPage ||
        !cards.length || cards.some((card) => Number(card.dataset.page) !== expectedPage)
      ) throw new Error('Collection page did not contain the expected products.');

      const existingIds = new Set(Array.from(grid.children).map((card) => card.dataset.productId));
      const newCards = cards.filter((card) => {
        if (existingIds.has(card.dataset.productId)) return false;
        existingIds.add(card.dataset.productId);
        return true;
      });
      if (!newCards.length) throw new Error('Collection page contained no new products.');

      grid.append(...newCards);
      this.dataset.currentPage = String(expectedPage);
      const nextLink = nextPagination.querySelector('a[data-load-more]');
      if (nextLink) {
        link.setAttribute('href', nextLink.getAttribute('href'));
      } else {
        // Keep keyboard focus within the newly available products on the final page.
        if (document.activeElement === link) newCards[0].querySelector('a[href]')?.focus({ preventScroll: true });
        link.remove();
      }
      status.textContent = `${newCards.length} more products loaded.${nextLink ? '' : ' You’ve reached the last page.'}`;
    } catch (error) {
      if (isCurrent()) {
        // The next click follows the original href, even if the asynchronous loader fails.
        link.setAttribute('data-load-more-fallback', '');
        status.textContent = 'We couldn’t load more products. Select “Open next page” to continue.';
      }
    } finally {
      this.#finishRequest(request);
    }
  };
}

if (!customElements.get('collection-pagination')) {
  customElements.define('collection-pagination', CollectionPagination);
}
